/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Badge,
  Tooltip,
  Popover,
  PopoverActions,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import { UploadFileComponent } from '@ink-ai/portal/common/components/ai-assistant/Compose/UploadFile';
import { ChatToolItemProps } from '../types';

export const UploadFileToolbarItem: React.FC<ChatToolItemProps> = ({
  disabled,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverActions = useRef<PopoverActions>();
  const referenceState = useSelector((state: RootState) => state.reference);

  const handleOpen = () => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={t('chat.uploadFile')} placement="top" arrow>
        <Badge
          badgeContent={referenceState.selectedFileIds.length}
          color="primary"
        >
          <span>
            <IconButton
              ref={buttonRef}
              color="primary"
              onClick={handleOpen}
              disabled={disabled}
              size="small"
            >
              <AttachFileIcon />
            </IconButton>
          </span>
        </Badge>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        action={popoverActions}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: 400,
            p: 2,
            transform: 'translate(100%, -100%)',
            mb: 1,
          },
        }}
      >
        <UploadFileComponent
          onFilesLoad={() => {
            if (popoverActions.current) {
              popoverActions.current.updatePosition();
            }
          }}
        />
      </Popover>
    </>
  );
};
